import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import EditNoteIcon from '@mui/icons-material/EditNote'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import PaymentIcon from '@mui/icons-material/Payment'
import type { SvgIconComponent } from '@mui/icons-material'
import type { NotificationKindFilter } from '../../api/contracts'

export type NotificationFilterKind = Exclude<NotificationKindFilter, 'all'>
export type NotificationLook = 'comment' | 'payment' | 'receipt' | 'status' | 'revision' | 'neutral'

export interface NotificationKind {
  /** The API `kind` filter that contains the event type; `null` for a type the client does not know yet. */
  filter: NotificationFilterKind | null
  look: NotificationLook
  Icon: SvgIconComponent
  background: string
  color: string
}

const LOOKS: Record<NotificationLook, Pick<NotificationKind, 'Icon' | 'background' | 'color'>> = {
  comment: { Icon: ChatBubbleOutlineIcon, background: '#e3ebfb', color: '#1a56c4' },
  payment: { Icon: PaymentIcon, background: '#e7f6ec', color: '#146c32' },
  receipt: { Icon: Inventory2OutlinedIcon, background: '#e7f6ec', color: '#146c32' },
  status: { Icon: FlagOutlinedIcon, background: '#fff4e5', color: '#9a3d06' },
  revision: { Icon: EditNoteIcon, background: '#eef1f6', color: '#5b6572' },
  neutral: { Icon: NotificationsNoneIcon, background: '#eef1f6', color: '#5b6572' }
}

// The grouping is the one of the API `NotificationKindFilter` description.
const EVENT_KINDS = {
  comment_added_v1: { filter: 'comments', look: 'comment' },
  invoice_added_v1: { filter: 'payments', look: 'payment' },
  invoice_sent_to_payment_v1: { filter: 'payments', look: 'payment' },
  request_sent_to_payment_v1: { filter: 'payments', look: 'payment' },
  payment_recorded_v1: { filter: 'payments', look: 'payment' },
  payment_reverted_v1: { filter: 'payments', look: 'payment' },
  receipt_recorded_v1: { filter: 'payments', look: 'receipt' },
  receipt_reverted_v1: { filter: 'payments', look: 'receipt' },
  request_created_v1: { filter: 'statuses', look: 'status' },
  assignee_changed_v1: { filter: 'statuses', look: 'status' },
  status_changed_v1: { filter: 'statuses', look: 'status' },
  request_deleted_state_changed_v1: { filter: 'statuses', look: 'status' },
  purchase_request_closed_v1: { filter: 'statuses', look: 'status' },
  request_revised_v1: { filter: 'statuses', look: 'revision' }
} as const satisfies Record<string, { filter: NotificationFilterKind; look: NotificationLook }>

export type NotificationEventType = keyof typeof EVENT_KINDS

export const NOTIFICATION_EVENT_TYPES = Object.keys(EVENT_KINDS) as readonly NotificationEventType[]

function isKnownEventType(eventType: string): eventType is NotificationEventType {
  return Object.prototype.hasOwnProperty.call(EVENT_KINDS, eventType)
}

export function getNotificationKind(eventType: string): NotificationKind {
  if (!isKnownEventType(eventType)) {
    return { filter: null, look: 'neutral', ...LOOKS.neutral }
  }
  const { filter, look } = EVENT_KINDS[eventType]
  return { filter, look, ...LOOKS[look] }
}
